import { useNavigate } from 'react-router-dom';
import { ImgWithSkeleton } from './ui/ImgWithSkeleton';
import type { TastingExperience, Media } from '@wineryxplorer/shared';
import { trpc } from '../lib/trpc';

interface TastingExperienceCardProps {
  experience: TastingExperience & {
    heroImage?: Media;
  };
}

interface WinePreview {
  _id: string;
  name: string;
  vintage?: number;
}

export const TastingExperienceCard = ({ experience }: TastingExperienceCardProps) => {
  const navigate = useNavigate();

  const wineIds = experience.wines ?? [];
  const venueWinesQuery = trpc.wine.listByVenue.useQuery(
    { venueId: experience.venueId, limit: 100 },
    { enabled: wineIds.length > 0 }
  );
  const wines = (venueWinesQuery.data?.items ?? []) as WinePreview[];
  const wineMap = new Map<string, WinePreview>(
    wines.map((w) => [w._id, w])
  );
  const orderedPouredWines = wineIds
    .map((id) => wineMap.get(id))
    .filter((w): w is WinePreview => Boolean(w));

  const handleClick = () => {
    navigate(`/tasting-experiences/${experience.id}`);
  };

  return (
    <div
      onClick={handleClick}
      className="bg-gray-100 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
    >
      {/* Hero Image */}
      {experience.heroImage ? (
        <div className="h-48 overflow-hidden">
          <ImgWithSkeleton
            src={experience.heroImage.cloudinaryUrl}
            alt={experience.name}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
            skeletonClassName="rounded-t-lg"
          />
        </div>
      ) : (
        <div className="h-48 bg-gray-200 flex items-center justify-center">
          <svg
            className="w-16 h-16 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
      )}

      <div className="p-6">
        {/* Badges */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {experience.wineClubOnly && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-800 border border-primary-200">
              Wine Club Exclusive
            </span>
          )}
          {experience.requiresReservation && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
              Reservation Required
            </span>
          )}
        </div>

        {/* Experience Name — editorial serif */}
        <h3 className="font-serif text-xl font-normal leading-tight text-gray-900 mb-2">
          {experience.name}
        </h3>

        {/* Price */}
        <div className="flex items-center text-sm text-gray-600 mb-3">
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="font-medium">
            ${experience.price.amount.toFixed(2)} {experience.price.currency}
          </span>
        </div>

        {/* Duration */}
        {experience.duration && (
          <div className="flex items-center text-sm text-gray-600 mb-3">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{experience.duration} minutes</span>
          </div>
        )}

        {/* Description Preview */}
        {experience.description && (
          <p className="text-sm text-gray-600 line-clamp-2 mt-3">
            {experience.description}
          </p>
        )}

        {orderedPouredWines.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="font-sans text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
              Pours
            </div>
            <ul className="space-y-1">
              {orderedPouredWines.map((wine) => (
                <li key={wine._id} className="font-serif text-base text-gray-900 leading-snug">
                  {wine.vintage && (
                    <span className="text-gray-600 mr-2 font-sans text-sm">{wine.vintage}</span>
                  )}
                  {wine.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
